import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "./AuthContext";

const ShopCtx = createContext(null);
const EMPTY = { items: [], count: 0, subtotal: 0, total: 0, shipping: 0, discount: 0, mrp_total: 0 };

export function ShopProvider({ children }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cart, setCart] = useState(EMPTY);
  const [wish, setWish] = useState([]);
  const [drops, setDrops] = useState({ count: 0, items: [] });
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    if (!user || ["employee", "rider"].includes(user.role)) { setCart(EMPTY); setWish([]); setDrops({ count: 0, items: [] }); setLoaded(user !== null); return; }
    const [c, w, d] = await Promise.all([api.get("/cart"), api.get("/wishlist"), api.get("/alerts/price-drops")]);
    setCart(c.data);
    setLoaded(true);
    setWish(w.data.map((p) => p.id));
    setDrops(d.data);
    if (d.data.count && !sessionStorage.getItem("dss_drop_toast")) {
      sessionStorage.setItem("dss_drop_toast", "1");
      toast.success(`Price dropped on ${d.data.count} wishlist item${d.data.count > 1 ? "s" : ""}!`, { action: { label: "View", onClick: () => nav("/wishlist") } });
    }
  }, [user]); // eslint-disable-line

  useEffect(() => { refresh().catch(() => {}); }, [refresh]);

  const needLogin = () => {
    if (user) return false;
    toast("Please login to continue");
    nav("/login", { state: { from: window.location.pathname + window.location.search } });
    return true;
  };

  const setQty = async (product_id, qty, silent, size = "") => {
    if (needLogin()) return false;
    try {
      const r = await api.post("/cart", { product_id, qty, size });
      setCart(r.data);
      if (!silent) toast.success(qty > 0 ? "Added to cart" : "Removed from cart");
      return true;
    } catch (e) { toast.error(errMsg(e)); return false; }
  };

  const addToCart = (p, qty = 1, size = "", silent = false) => {
    if (p.sizes?.length && !size) { toast.error("Please select a size"); nav(`/product/${p.id}`); return Promise.resolve(false); }
    const line = cart.items.find((l) => l.product.id === p.id && (l.size || "") === size);
    return setQty(p.id, Math.min(20, (line?.qty || 0) + qty), silent, size);
  };

  const toggleWish = async (pid) => {
    if (needLogin()) return;
    const r = await api.post(`/wishlist/${pid}`);
    setWish(r.data.product_ids);
    api.get("/alerts/price-drops").then((d) => setDrops(d.data));
    toast.success(r.data.added ? "Saved to wishlist" : "Removed from wishlist");
  };

  return (
    <ShopCtx.Provider value={{ cart, setCart, loaded, wish, drops, setDrops, setQty, addToCart, toggleWish, refresh }}>
      {children}
    </ShopCtx.Provider>
  );
}

export const useShop = () => useContext(ShopCtx);
